import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useCreateMember } from '../../hooks/useQueries';
import type { Member, CreateMemberRequest } from '../../services';

const quickMemberSchema = z.object({
  name: z.string().min(1, 'ชื่อเป็นข้อมูลที่จำเป็น'),
  phoneNumber: z.string().min(10, 'หมายเลขโทรศัพท์ต้องมีอย่างน้อย 10 หลัก'),
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').optional().or(z.literal('')),
});

type QuickMemberFormData = z.infer<typeof quickMemberSchema>;

interface QuickAddMemberProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMemberAdded: (member: Member) => void;
}

export function QuickAddMember({ open, onOpenChange, onMemberAdded }: QuickAddMemberProps) {
  const form = useForm<QuickMemberFormData>({
    resolver: zodResolver(quickMemberSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      email: '',
    },
  });

  // Create member mutation
  const createMemberMutation = useCreateMember();

  // Handle success
  useEffect(() => {
    if (createMemberMutation.isSuccess && createMemberMutation.data) {
      toast.success('เพิ่มสมาชิกใหม่สำเร็จ');
      onMemberAdded(createMemberMutation.data);
      form.reset();
      onOpenChange(false);
    }
  }, [createMemberMutation.isSuccess, createMemberMutation.data, onMemberAdded, form, onOpenChange]);

  // Handle error
  useEffect(() => {
    if (createMemberMutation.isError) {
      toast.error('เกิดข้อผิดพลาดในการเพิ่มสมาชิก');
    }
  }, [createMemberMutation.isError]);

  const handleSubmit = (data: QuickMemberFormData) => {
    const memberData: CreateMemberRequest = {
      name: data.name,
      phoneNumber: data.phoneNumber,
      email: data.email || undefined,
      role: 'member' as const,
    };
    
    createMemberMutation.mutate(memberData);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>เพิ่มสมาชิกใหม่</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ชื่อ-นามสกุล *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="กรอกชื่อ-นามสกุล"
                      {...field}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>หมายเลขโทรศัพท์ *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="081-234-5678"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>อีเมล</FormLabel>
                  <FormControl>
                    <Input 
                      type="email"
                      placeholder="example@email.com (ไม่จำเป็น)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                disabled={createMemberMutation.isPending}
              >
                ยกเลิก
              </Button>
              <Button 
                type="submit" 
                disabled={createMemberMutation.isPending}
              >
                {createMemberMutation.isPending ? 'กำลังบันทึก...' : 'เพิ่มสมาชิก'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}